import React, { useState, useEffect, useRef } from 'react';
import UnsavedChangesGuard from '../components/UnsavedChangesGuard';
import { useUnsavedChangesContext } from '../hooks/useUnsavedChangesWarning';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Collapse,
  Card,
  CardContent,
  IconButton,
  Fab,
} from '@mui/material';
import { Save as SaveIcon, PhotoCamera as PhotoIcon, ExpandMore as ExpandMoreIcon, ExpandLess as ExpandLessIcon } from '@mui/icons-material';
import { useForm, Controller } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';

import PhotoUpload from '../components/PhotoUpload/PhotoUpload';
import { livresAPI, auteursAPI, editeursAPI, bibliotequesAPI, rayonnagesAPI, tagsAPI } from '../services/api';
import AuteurSelector from '../components/AuteurSelector';
import EditeurSelector from '../components/EditeurSelector';
import TagSelector from '../components/TagSelector';
import BibliothequeRayonnageSelector from '../components/BibliothequeRayonnageSelector';
import EtatLivreSelector from '../components/EtatLivreSelector';
import { getStatutsLectureTranslated } from '../utils/statusUtils';

// Fonction pour normaliser les accents et caractères spéciaux
const normalizeString = (str) => {
  return str
    .toLowerCase()
    .normalize('NFD') // Décompose les caractères accentués
    .replace(/[\u0300-\u036f]/g, '') // Supprime les diacritiques
    .replace(/[^a-z0-9\s]/g, '') // Supprime les caractères spéciaux
    .replace(/\s+/g, ' ') // Normalise les espaces
    .trim();
};

function AjouterLivrePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { setHasUnsavedChanges: setGlobalHasUnsavedChanges } = useUnsavedChangesContext();
  
  // Statuts de lecture avec traductions (sans l'option "Tous les statuts")
  const statutsLecture = getStatutsLectureTranslated(t, false);
  const [uploadedPhotos, setUploadedPhotos] = useState({});
  const [detailsExpanded, setDetailsExpanded] = useState(false);
  const [ocrAlerts, setOcrAlerts] = useState([]);
  const photoUploadRef = useRef(null);
  
  // Mémorisation de la bibliothèque et du rayonnage
  const [lastBibliothequeId, setLastBibliothequeId] = useState(() => {
    return localStorage.getItem('lastBibliothequeId') || '';
  });
  const [lastRayonnageId, setLastRayonnageId] = useState(() => {
    return localStorage.getItem('lastRayonnageId') || '';
  });

  const { control, handleSubmit, setValue, watch, formState: { errors } } = useForm({
    defaultValues: {
      titre: '',
      edition: '',
      nombre_pages: '',
      annee_publication: '',
      isbn: '',
      resume: '',
      notes_personnelles: '',
      statut_lecture: 'indefini',
      etat_livre: 'indefini',
      note_personnelle: '',
      auteurs: [],
      editeur: null,
      tags: [],
      bibliotheque_id: lastBibliothequeId,
      rayonnage_id: lastRayonnageId
    }
  });

  // État pour la recherche d'auteurs
  const [auteurSearchTerm, setAuteurSearchTerm] = useState('');

  // Requête pour rechercher les auteurs de manière incrémentale
  const { error: auteursError } = useQuery(
    ['auteurs-search', auteurSearchTerm],
    () => auteursAPI.search(auteurSearchTerm).then(res => res.data.data),
    { 
      enabled: auteurSearchTerm.length >= 2, // Recherche seulement à partir de 2 caractères
      staleTime: 2 * 60 * 1000, // 2 minutes
      cacheTime: 5 * 60 * 1000 // 5 minutes
    }
  );

  // Requête pour récupérer les éditeurs existants
  const { error: editeursError } = useQuery(
    'editeurs-list',
    () => editeursAPI.getAll().then(res => res.data.data),
    { 
      staleTime: 5 * 60 * 1000, // 5 minutes
      cacheTime: 10 * 60 * 1000 // 10 minutes
    }
  );

  // Requête pour récupérer les bibliothèques
  useQuery(
    'bibliotheques-list',
    () => bibliotequesAPI.getAll().then(res => res.data.data),
    { 
      staleTime: 5 * 60 * 1000, // 5 minutes
      cacheTime: 10 * 60 * 1000 // 10 minutes
    }
  );

  // Requête pour récupérer les rayonnages
  useQuery(
    'rayonnages-list',
    () => rayonnagesAPI.getAll().then(res => res.data.data),
    { 
      staleTime: 5 * 60 * 1000, // 5 minutes
      cacheTime: 10 * 60 * 1000 // 10 minutes
    }
  );

  // Requête pour récupérer les tags
  useQuery(
    'tags-list',
    () => tagsAPI.getAll().then(res => res.data.data),
    { 
      staleTime: 5 * 60 * 1000, // 5 minutes
      cacheTime: 10 * 60 * 1000 // 10 minutes
    }
  );

  // Bloquer la navigation si le formulaire a été modifié
  const formValues = watch();
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  useEffect(() => {
    // Vérifier si le formulaire a été modifié (titre rempli ou photo uploadée)
    const hasTitleFilled = formValues.titre && formValues.titre.trim().length > 0;
    const hasPhotos = Object.keys(uploadedPhotos).length > 0;
    const isFormDirty = hasTitleFilled || hasPhotos;
    setHasUnsavedChanges(isFormDirty);
    setGlobalHasUnsavedChanges(isFormDirty, t('addBookPage.unsavedChanges'));
  }, [formValues, uploadedPhotos, setGlobalHasUnsavedChanges]);

  // Plus besoin de allowNavigation, c'est le Guard qui gère tout

  // Mutation pour créer le livre
  const createLivreMutation = useMutation(
    (data) => livresAPI.create(data),
    {
      onSuccess: (response) => {
        // Réinitialiser le flag de modifications non sauvegardées dans le contexte global
        setHasUnsavedChanges(false);
        setGlobalHasUnsavedChanges(false);
        
        // Invalider les caches pour rafraîchir les données
        queryClient.invalidateQueries('auteurs-list');
        queryClient.invalidateQueries('editeurs-list');
        queryClient.invalidateQueries('tags-list');
        queryClient.invalidateQueries('livres');
        
        toast.success(t('addBookPage.saveSuccess'));
        navigate(`/livres/${response.data.data.id}`);
      },
      onError: (error) => {
        console.error('Erreur lors de l\'ajout:', error);
        
        // Afficher les erreurs de validation du serveur
        if (error.response?.data?.errors) {
          const errors = error.response.data.errors;
          console.log('Erreurs de validation:', errors);
          
          // Afficher chaque erreur
          errors.forEach(err => {
            if (err.message) {
              toast.error(err.message);
            }
          });
        } else if (error.response?.data?.message) {
          // Afficher le message d'erreur du serveur
          toast.error(error.response.data.message);
          console.error('Détails de l\'erreur:', error.response.data);
        } else if (error.message) {
          // Afficher le message d'erreur générique avec plus de détails
          toast.error(`Erreur: ${error.message}`);
          console.error('Erreur complète:', error);
        } else {
          toast.error(t('addBookPage.saveError'));
          console.error('Erreur inconnue:', error);
        }
      }
    }
  );

  const handleOCRResult = async (ocrResult) => {
    if (ocrResult.success && ocrResult.metadata) {
      const metadata = ocrResult.metadata;

      // Pré-remplir les champs avec les données OCR
      if (metadata.titre) setValue('titre', metadata.titre, { shouldValidate: true });
      if (metadata.isbn) setValue('isbn', metadata.isbn, { shouldValidate: true });
      if (metadata.annee) setValue('annee_publication', metadata.annee, { shouldValidate: true });
      
      // Traiter l'auteur OCR
      if (metadata.auteur) {
        console.log('📖 Auteur détecté par OCR:', metadata.auteur);
        
        // Rechercher l'auteur via l'API
        try {
          console.log('🔍 Recherche auteur OCR:', metadata.auteur);
          const searchResult = await auteursAPI.search(metadata.auteur);
          console.log('📋 Résultats recherche:', searchResult.data.data);
          
          const auteurExistant = searchResult.data.data?.find(auteur => {
            const nomComplet = `${auteur.prenom || ''} ${auteur.nom}`.trim();
            const isMatch = normalizeString(nomComplet) === normalizeString(metadata.auteur);
            console.log(`🔍 Comparaison: "${normalizeString(nomComplet)}" === "${normalizeString(metadata.auteur)}" = ${isMatch}`);
            return isMatch;
          });
        
        let auteurOCR;
        console.log('🎯 Auteur existant trouvé:', auteurExistant);
        if (auteurExistant) {
          // Auteur existant trouvé
          auteurOCR = {
            id: auteurExistant.id,
            nom: auteurExistant.nom,
            prenom: auteurExistant.prenom,
            displayName: `${auteurExistant.prenom || ''} ${auteurExistant.nom}`.trim()
          };
          console.log('✅ Auteur existant trouvé:', auteurOCR);
        } else {
          // Nouvel auteur
          const [prenom, ...nomParts] = metadata.auteur.trim().split(' ');
          const nom = nomParts.join(' ') || prenom;
          const prenomFinal = nomParts.length > 0 ? prenom : '';
          
          auteurOCR = {
            isNew: true,
            nom: nom,
            prenom: prenomFinal,
            displayName: metadata.auteur.trim()
          };
          console.log('🆕 Nouvel auteur créé:', auteurOCR);
        }
        
        // Récupérer les auteurs actuels et ajouter l'auteur OCR
        const auteursActuels = watch('auteurs') || [];
        const auteursAvecOCR = [...auteursActuels, auteurOCR];
        setValue('auteurs', auteursAvecOCR, { shouldValidate: true });
        } catch (error) {
          console.error('Erreur lors de la recherche d\'auteur:', error);
          // En cas d'erreur, créer un nouvel auteur
          const [prenom, ...nomParts] = metadata.auteur.trim().split(' ');
          const nom = nomParts.join(' ') || prenom;
          const prenomFinal = nomParts.length > 0 ? prenom : '';
          
          const auteurOCR = {
            isNew: true,
            nom: nom,
            prenom: prenomFinal,
            displayName: metadata.auteur.trim()
          };
          
          const auteursActuels = watch('auteurs') || [];
          setValue('auteurs', [...auteursActuels, auteurOCR], { shouldValidate: true });
        }
      }
      
      // Traiter l'éditeur OCR
      if (metadata.editeur) {
        console.log('🏢 Éditeur détecté par OCR:', metadata.editeur);
        try {
          // Rechercher l'éditeur spécifiquement par nom
          console.log('🔍 Recherche éditeur OCR:', metadata.editeur);
          const searchResult = await editeursAPI.search(metadata.editeur);
          console.log('📋 Résultats recherche éditeur:', searchResult.data.data);
          
          const editeurExistant = searchResult.data.data?.find(editeur => {
            const isMatch = normalizeString(editeur.nom) === normalizeString(metadata.editeur);
            console.log(`🔍 Comparaison éditeur: "${normalizeString(editeur.nom)}" === "${normalizeString(metadata.editeur)}" = ${isMatch}`);
            return isMatch;
          });
        
          let editeurOCR;
          console.log('🎯 Éditeur existant trouvé:', editeurExistant);
          if (editeurExistant) {
            // Éditeur existant trouvé
            editeurOCR = {
              id: editeurExistant.id,
              nom: editeurExistant.nom,
              displayName: editeurExistant.nom
            };
            console.log('✅ Éditeur existant trouvé:', editeurOCR);
          } else {
            // Nouvel éditeur
            editeurOCR = {
              isNew: true,
              nom: metadata.editeur.trim(),
              displayName: metadata.editeur.trim()
            };
            console.log('🆕 Nouvel éditeur créé:', editeurOCR);
          }
          
          setValue('editeur', editeurOCR, { shouldValidate: true });
        } catch (error) {
          console.error('Erreur lors de la recherche d\'éditeur:', error);
          // En cas d'erreur, créer un nouvel éditeur
          const editeurOCR = {
            isNew: true,
            nom: metadata.editeur.trim(),
            displayName: metadata.editeur.trim()
          };
          
          setValue('editeur', editeurOCR, { shouldValidate: true });
        }
      }

      // Vérifier si le livre existe déjà dans la base
      if (metadata.titre) {
        try {
          // Utiliser une recherche partielle pour trouver des titres similaires
          const response = await livresAPI.getAll({ 
            search: metadata.titre,
            limit: 10 // Augmenter la limite pour vérifier plusieurs résultats
          });
          
          if (response.data.success && response.data.data.length > 0) {
            // Chercher une correspondance exacte parmi les résultats
            const livreExistant = response.data.data.find(livre => 
              livre.titre && 
              livre.titre.toLowerCase().trim() === metadata.titre.toLowerCase().trim()
            );
            
            if (livreExistant) {
              const bibliothequeInfo = livreExistant.bibliotheque_nom ? 
                `${livreExistant.bibliotheque_nom}${livreExistant.bibliotheque_piece ? ` - ${livreExistant.bibliotheque_piece}` : ''}` : '';
              const rayonnageInfo = livreExistant.rayonnage_nom ? 
                livreExistant.rayonnage_nom : '';
              
              // Construire le message selon ce qui est disponible
              let message = '';
              if (bibliothequeInfo && rayonnageInfo) {
                message = `${t('addBookPage.library')} ${bibliothequeInfo}, ${t('addBookPage.shelf')} ${rayonnageInfo}`;
              } else if (bibliothequeInfo) {
                message = `${t('addBookPage.library')} ${bibliothequeInfo}`;
              } else if (rayonnageInfo) {
                message = `${t('addBookPage.shelf')} ${rayonnageInfo}`;
              }
              
              setOcrAlerts([{
                severity: 'warning',
                title: t('addBookPage.bookMayExist'),
                message: message || undefined
              }]);
            } else {
              /*setOcrAlerts([{
                severity: 'success',
                title: 'OCR réussi',
                message: 'Informations extraites avec succès ! Vérifiez et complétez les champs ci-dessous.'
              }]);*/
            }
          } else {
            /*setOcrAlerts([{
              severity: 'success',
              title: 'OCR réussi',
              message: 'Informations extraites avec succès ! Vérifiez et complétez les champs ci-dessous.'
            }]);*/
          }
        } catch (error) {
          console.error("Erreur lors de la vérification de l'existence du livre:", error);
          /*setOcrAlerts([{
            severity: 'success',
            title: 'OCR réussi',
            message: 'Informations extraites avec succès ! Vérifiez et complétez les champs ci-dessous.'
          }]);*/
        }
      } else {
        /*setOcrAlerts([{
          severity: 'success',
          title: 'OCR réussi',
          message: 'Informations extraites avec succès ! Vérifiez et complétez les champs ci-dessous.'
        }]);*/
      }
    }
  };

  const handlePhotoUploaded = (photoData) => {
    // Ne garder que la dernière photo (remplacer au lieu d'ajouter)
    setUploadedPhotos({
      couverture: photoData
    });
  };

  const onSubmit = async (data) => {
    try {
      console.log('📋 Données du formulaire avant envoi:', data);
      console.log('👥 Auteurs sélectionnés:', data.auteurs);
      
      const formData = new FormData();

      // Ajouter les données du livre
      Object.keys(data).forEach(key => {
        // Ne pas envoyer bibliotheque_id car il n'est pas utilisé côté serveur
        if (key === 'bibliotheque_id') {
          return;
        }
        
        if (data[key] !== '' && data[key] !== null && data[key] !== undefined) {
          if (key === 'auteurs' || key === 'tags' || key === 'editeur') {
            const jsonData = JSON.stringify(data[key]);
            console.log(`📤 Envoi ${key}:`, jsonData);
            formData.append(key, jsonData);
          } else {
            formData.append(key, data[key]);
          }
        }
      });

      // Ajouter les photos
      Object.entries(uploadedPhotos).forEach(([type, photoData]) => {
        if (photoData.file) {
          formData.append(type, photoData.file);
        }
      });

      await createLivreMutation.mutateAsync(formData);
      
      // Mémoriser la bibliothèque et le rayonnage pour le prochain livre (même si vides)
      setLastBibliothequeId(data.bibliotheque_id || '');
      localStorage.setItem('lastBibliothequeId', data.bibliotheque_id || '');
      setLastRayonnageId(data.rayonnage_id || '');
      localStorage.setItem('lastRayonnageId', data.rayonnage_id || '');
    } catch (error) {
      console.error('❌ Erreur soumission:', error);
    }
  };

  const renderStepContent = () => {
        return (
          <div>
          <Box>
            <Typography variant="h6" gutterBottom>
              {t('addBookPage.photoTitle')}
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              {t('addBookPage.photoDescription')}
            </Typography>
            <PhotoUpload
              ref={photoUploadRef}
              onOCRResult={handleOCRResult}
              onPhotoUploaded={handlePhotoUploaded}
              label={t('addBookPage.addPhotoLabel')}
              singlePhotoMode={true}
            />
            
            {/* Alertes OCR */}
            {ocrAlerts.map((alert, index) => (
              <Alert 
                key={index}
                severity={alert.severity}
                onClose={() => setOcrAlerts(prev => prev.filter((_, i) => i !== index))}
                sx={{ mt: 2 }}
              >
                <Typography variant="subtitle2" fontWeight="bold">
                  {alert.title}
                </Typography>
                <Typography variant="body2">
                  {alert.message}
                </Typography>
              </Alert>
            ))}
          </Box>
          <Grid container spacing={3} sx={{ mt: 2 }}>
            <Grid item xs={12}>
              <Controller
                name="titre"
                control={control}
                rules={{ required: t('addBookPage.titleRequired') }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label={t('addBookPage.titleLabel')}
                    error={!!errors.titre}
                    helperText={errors.titre?.message}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                      }
                    }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              {auteursError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {t('addBookPage.authorsError', { message: auteursError.message })}
                </Alert>
              )}
              <AuteurSelector
                control={control}
                name="auteurs"
                label={t('addBookPage.authorsLabel')}
                disabled={createLivreMutation.isLoading}
              />
            </Grid>
            <Grid item xs={12}>
              {editeursError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {t('addBookPage.publishersError', { message: editeursError.message })}
                </Alert>
              )}
              <EditeurSelector
                control={control}
                name="editeur"
                label={t('addBookPage.publisherLabel')}
                disabled={createLivreMutation.isLoading}
              />
            </Grid>
            <Grid item xs={12}>
              <TagSelector
                control={control}
                name="tags"
                disabled={createLivreMutation.isLoading}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller
                name="statut_lecture"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth>
                    <InputLabel>{t('addBookPage.readingStatusLabel')}</InputLabel>
                    <Select 
                      {...field} 
                      label={t('addBookPage.readingStatusLabel')}
                      sx={{
                        borderRadius: 2,
                      }}
                    >
                      {statutsLecture.map((statut) => (
                        <MenuItem key={statut.value} value={statut.value}>
                          {statut.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <Controller
                name="notes_personnelles"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label={t('addBookPage.personalNotes')}
                    multiline
                    rows={4}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                      }
                    }}
                  />
                )}
              />
            </Grid>
          </Grid>

          {/* Section Localisation */}
          <Grid container spacing={3} sx={{ mt: 0 }}>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                {t('addBookPage.location')}
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <BibliothequeRayonnageSelector 
                control={control} 
                watch={watch}
                setValue={setValue}
                disabled={createLivreMutation.isLoading}
                onBibliothequeChange={(value) => {
                  // Mémoriser même les valeurs vides
                  setLastBibliothequeId(value || '');
                  localStorage.setItem('lastBibliothequeId', value || '');
                }}
                onRayonnageChange={(value) => {
                  // Mémoriser même les valeurs vides
                  setLastRayonnageId(value || '');
                  localStorage.setItem('lastRayonnageId', value || '');
                }}
              />
            </Grid>
          </Grid>
          </div>
        );

  };

  return (
    <Box>
      <Typography variant="h4" component="h1" gutterBottom>
        {t('addBookPage.title')}
      </Typography>

      <Paper sx={{ p: 3, borderRadius: 2 }}>
                <Box sx={{ mb: 2 }}>
                  {renderStepContent()}
                </Box>
                {/* Section Détails */}
                <Box sx={{ mb: 2 }}>
                  <Card sx={{ borderRadius: 2 }}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        p: 2,
                        cursor: 'pointer',
                        '&:hover': {
                          backgroundColor: 'action.hover'
                        }
                      }}
                      onClick={() => setDetailsExpanded(!detailsExpanded)}
                    >
                      <Typography variant="h6">
                        {t('addBookPage.details')}
                      </Typography>
                      <IconButton size="small">
                        {detailsExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      </IconButton>
                    </Box>
                    <Collapse in={detailsExpanded}>
                      <CardContent sx={{ pt: 0 }}>
                        <Grid container spacing={2}>

                          <Grid item xs={12} sm={6}>
                            <Controller
                              name="edition"
                              control={control}
                              render={({ field }) => (
                                <TextField
                                  {...field}
                                  fullWidth
                                  label={t('addBookPage.edition')}
                                  sx={{
                                    '& .MuiOutlinedInput-root': {
                                      borderRadius: 2,
                                    }
                                  }}
                                />
                              )}
                            />
                          </Grid>

                          <Grid item xs={12} sm={6}>
                            <Controller
                              name="isbn"
                              control={control}
                              render={({ field }) => (
                                <TextField
                                  {...field}
                                  fullWidth
                                  label={t('addBookPage.isbn')}
                                  placeholder={t('addBookPage.isbnPlaceholder')}
                                  sx={{
                                    '& .MuiOutlinedInput-root': {
                                      borderRadius: 2,
                                    }
                                  }}
                                />
                              )}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <Controller
                              name="nombre_pages"
                              control={control}
                              render={({ field }) => (
                                <TextField
                                  {...field}
                                  fullWidth
                                  label={t('addBookPage.pageCount')}
                                  type="number"
                                  sx={{
                                    '& .MuiOutlinedInput-root': {
                                      borderRadius: 2,
                                    }
                                  }}
                                />
                              )}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <Controller
                              name="annee_publication"
                              control={control}
                              render={({ field }) => (
                                <TextField
                                  {...field}
                                  fullWidth
                                  label={t('addBookPage.publicationYear')}
                                  type="number"
                                  sx={{
                                    '& .MuiOutlinedInput-root': {
                                      borderRadius: 2,
                                    }
                                  }}
                                />
                              )}
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <Controller
                              name="resume"
                              control={control}
                              render={({ field }) => (
                                <TextField
                                  {...field}
                                  fullWidth
                                  label={t('addBookPage.summary')}
                                  multiline
                                  rows={4}
                                  sx={{
                                    '& .MuiOutlinedInput-root': {
                                      borderRadius: 2,
                                    }
                                  }}
                                />
                              )}
                            />
                          </Grid>

                          <Grid item xs={12} sm={6}>
                            <EtatLivreSelector
                              control={control}
                              name="etat_livre"
                            />
                          </Grid>

                        </Grid>
                      </CardContent>
                    </Collapse>
                  </Card>
                </Box>

                <Box sx={{ mb: 1 }}>
                  <Button
                    variant="contained"
                    onClick={handleSubmit(onSubmit)}
                    sx={{ 
                      mr: 1,
                      borderRadius: 2,
                    }}
                    disabled={createLivreMutation.isLoading}
                    startIcon={<SaveIcon />}
                  >
                    {t('addBookPage.save')}
                  </Button>
                </Box>

      </Paper>

      {/* Bouton flottant pour prendre une photo */}
        <Fab
          color="primary"
          size="large"
          onClick={() => {
            if (photoUploadRef.current) {
              photoUploadRef.current.triggerCamera();
            }
          }}
          sx={{
            position: 'fixed',
            bottom: { xs: 16, sm: 24, md: 32 },
            right: { xs: 16, sm: 24, md: 32 },
            zIndex: 9999,
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            '&:hover': {
              boxShadow: '0 6px 16px rgba(0,0,0,0.2)',
            }
          }}
        >
          <PhotoIcon />
        </Fab>

      {/* Guard pour bloquer la navigation en cas de modifications non sauvegardées */}
      <UnsavedChangesGuard
        hasUnsavedChanges={hasUnsavedChanges}
        message={t('addBookPage.unsavedChanges')}
      />
    </Box>
  );
}

export default AjouterLivrePage;
